import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';

import { CheckboxFilterGroup, FilterPanel } from '../../../generic/Filter';
import * as ProjectsV2Store from '../../../../redux/projectsV2';
import useFetchProjectsAction from '../hooks/useFetchProjectsAction';
import type { ProjectsFilter, ProjectType as ProjType } from '../../../../../shared/projectsV2';
import { FilterKeys } from '../../../../../shared/projectsV2';
import type { FilterOption } from '../../../generic/Filter/CheckboxFilterGroup';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import { BINARY_DECOMPOSITION } from '../../../../../shared/featureFlags/flagNames';

const FILTER_OPTIONS: FilterOption[] = [
  { key: 'provided', label: 'CLI upload' },
  { key: 'container', label: 'Container' },
  { key: 'archive', label: 'Archive upload' },
  { key: 'autobuild', label: 'Quick import' },
  { key: 'sbom', label: 'SBOM import' },
  { key: 'binary', label: 'Binary Decomposition' },
];

const ProjectType: VoidFunctionComponent = () => {
  const filter = useSelector(ProjectsV2Store.selectors.getFilter) as ProjectsFilter;
  const setFilter = useFetchProjectsAction(ProjectsV2Store.actions.setFilter);

  const onSelect = (key: string, selected: boolean) => {
    const type = key as ProjType;

    // add if selected and not present
    if (selected && !filter.type?.includes(type)) {
      const current = filter.type ?? [];
      const added = [...current, type];
      setFilter({ ...filter, [FilterKeys.Type]: added });
    }

    // remove if unselected and present
    if (!selected && filter.type?.includes(type)) {
      const current = filter.type ?? [];
      const removed = current.filter(t => t !== type);
      setFilter({ ...filter, [FilterKeys.Type]: removed });
    }
  };

  // Binary Decomposition project attribute should only be displayed when feature is enabled.
  const isBinaryDecompositionEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  const options = FILTER_OPTIONS.filter(
    entry => (isBinaryDecompositionEnabled || entry.key !== 'binary'));

  return (
    <FilterPanel>
      <CheckboxFilterGroup
        title="Project attributes"
        options={options}
        selectedOptions={filter.type}
        onSelect={onSelect}
      />
    </FilterPanel>
  );
};

export default ProjectType;
